import React, { useState, useEffect } from "react";
import { Plus, Eye, Check, X, Upload, Download, FileText, Clock, AlertCircle, CheckCircle, XCircle } from "lucide-react";
import apiService from "../../services/apiService";

const STORAGE_BASE_URL = "http://api.sunrisesourcings.com/storage/app/public/";

const ClaimsManager = ({ userRole }) => {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSubmitForm, setShowSubmitForm] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [claimDetails, setClaimDetails] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("submission");
  const [user, setUser] = useState(null);

  // Fetch claims from API
  const getClaimsAsync = async () => {
    try {
      let queryString = {};
      const userString = localStorage.getItem("health_user");
      var rUser = JSON.parse(userString);
      if (rUser?.role !== "admin") {
        queryString.search = rUser?.member?.member_number;
      }
      const result = await apiService.getClaims(queryString);
      setClaims(result.data);
    } catch (err) {
      console.error("Failed to load claims:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch claim details
  const getClaimDetailsAsync = async (claimId) => {
    try {
      setDetailsLoading(true);
      const result = await apiService.getClaimById(claimId);
      setClaimDetails(result);
    } catch (err) {
      console.error("Failed to load claim details:", err);
    } finally {
      setDetailsLoading(false);
    }
  };

  useEffect(() => {
    const userString = localStorage.getItem("health_user");
    if (userString) {
      setUser(JSON.parse(userString));
    }
    getClaimsAsync();
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
      case "paid":
        return "bg-green-100 text-green-800";
      case "denied":
        return "bg-red-100 text-red-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "processing":
      case "under_review":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const handleReviewClaim = async (id, decision) => {
    try {
      const body = {
        decision,
        notes:
          decision === "approve"
            ? "Claim approved after thorough review. All documentation is in order."
            : "Claim denied due to insufficient documentation.",
      };
      await apiService.reviewClaim(id, body);
      setSelectedClaim(null);
      setDetailsModalOpen(false);
      getClaimsAsync();
    } catch (err) {
      console.error("Review claim failed:", err);
    }
  };

  const handleSubmitClaim = async (formData, files, onClose) => {
    try {
      const claimData = {
        member_id: formData.member_id,
        policy_id: formData.policy_id,
        incident_date: formData.incidentDate,
        description: formData.description,
        amount_claimed: formData.amount,
        provider_name: formData.provider,
        claim_type: formData.type,
        documents: files,
      };
      await apiService.submitClaim(claimData);
      onClose();
      getClaimsAsync();
    } catch (err) {
      console.error("Submit claim failed:", err);
    }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      await apiService.updateClaimStatus(id, { status });
      setDetailsModalOpen(false);
      setSelectedClaim(null);
      setClaimDetails(null);
      getClaimsAsync();
    } catch (err) {
      console.error("Update status failed:", err);
    }
  };

  const handleReleasePayment = async (id) => {
    try {
      await apiService.makeRequest(`/claims/${id}/release-payment`, {
        method: "POST",
      });
      getClaimsAsync();
      // Refresh details
      await getClaimDetailsAsync(id);
    } catch (err) {
      console.error("Release payment failed:", err);
    }
  };

  const openDetailsModal = async (claim) => {
    setSelectedClaim(claim);
    setDetailsModalOpen(true);
    setActiveTab("submission");
    await getClaimDetailsAsync(claim.id);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / 1048576).toFixed(1) + " MB";
  };
// Claim Submission Form Component
const ClaimSubmissionForm = ({ onClose }) => {
  const [formData, setFormData] = useState({
    member_id: "",
    policy_id: "",
    type: "",
    amount: "",
    description: "",
    incidentDate: "",
    provider: "",
  });
  const [files, setFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [members, setMembers] = useState([]);
  const [memberPolicies, setMemberPolicies] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [loadingPolicies, setLoadingPolicies] = useState(false);
  const [submitting, setSubmitting] = useState(false); 

  // Fetch members for admin
  useEffect(() => {
    if (userRole === "admin") {
      fetchMembers();
    }
  }, []);

  const fetchMembers = async () => {
    try {
      setLoadingMembers(true);
      const result = await apiService.getMembers();
      if (result && Array.isArray(result?.data)) {
        setMembers(result.data);
      }
    } catch (err) {
      console.error("Failed to load members:", err);
    } finally {
      setLoadingMembers(false);
    }
  };

  // Fetch policies for selected member
  const fetchMemberPolicies = async (memberId) => {
    try {
      setLoadingPolicies(true);
      const result = await apiService.getMemberById(memberId);
      if (result?.policies) {
        setMemberPolicies(result.policies);
      }
    } catch (err) {
      console.error("Failed to load member policies:", err);
    } finally {
      setLoadingPolicies(false);
    }
  };

  const handleMemberChange = (memberId) => {
    setFormData({ ...formData, member_id: memberId, policy_id: "" });
    setMemberPolicies([]);
    if (memberId) {
      fetchMemberPolicies(memberId);
    }
  };

  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  // Handle drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const newFiles = Array.from(e.dataTransfer.files);
      setFiles((prev) => [...prev, ...newFiles]);
    }
  };

  // Handle file input change
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      setFiles((prev) => [...prev, ...newFiles]);
    }
  };

  // Remove file
  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true); // Start loading
    
    try {
      await handleSubmitClaim(formData, files, onClose);
    } catch (err) {
      console.error("Submit failed:", err);
    } finally {
      setSubmitting(false); // Stop loading
    }
  };


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-900">
            Submit New Claim
          </h3>
        </div>

        {/* Form Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          <form id="claim-form" onSubmit={handleSubmit} className="space-y-4">
            {/* Member Selection - Only for Admin */}
            {userRole === "admin" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Member *
                </label>
                <select
                  value={formData.member_id}
                  onChange={(e) => handleMemberChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                  disabled={loadingMembers}
                >
                  <option value="">
                    {loadingMembers ? "Loading members..." : "Select a member"}
                  </option>
                  {members.map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.user?.name} - {member.member_number}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Policy Selection */}
            {((userRole === "admin" && formData.member_id) || userRole === "member") && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Policy *
                </label>
                <select
                  value={formData.policy_id}
                  onChange={(e) =>
                    setFormData({ ...formData, policy_id: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                  disabled={loadingPolicies || (userRole === "admin" && !formData.member_id)}
                >
                  <option value="">
                    {loadingPolicies ? "Loading policies..." : "Select a policy"}
                  </option>
                  {memberPolicies.map((policy) => (
                    <option key={policy.id} value={policy.id}>
                      {policy.name} - ${policy.premium_amount}/month
                    </option>
                  ))}
                </select>
                {userRole === "admin" && !formData.member_id && (
                  <p className="text-xs text-gray-500 mt-1">
                    Select a member first to view their policies
                  </p>
                )}
              </div>
            )}

            {/* Claim Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Claim Type *
              </label>
              <select
                value={formData.type}
                onChange={(e) =>
                  setFormData({ ...formData, type: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select type</option>
                <option value="medical">Medical</option>
                <option value="dental">Dental</option>
                <option value="vision">Vision</option>
              </select>
            </div>

            {/* Amount and Incident Date */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount ($) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) =>
                    setFormData({ ...formData, amount: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Incident Date *
                </label>
                <input
                  type="date"
                  value={formData.incidentDate}
                  onChange={(e) =>
                    setFormData({ ...formData, incidentDate: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>

            {/* Healthcare Provider */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Healthcare Provider *
              </label>
              <input
                type="text"
                value={formData.provider}
                onChange={(e) =>
                  setFormData({ ...formData, provider: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Enter provider name"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                rows="3"
                placeholder="Describe the incident and treatment received"
                required
              />
            </div>

            {/* File Upload - Drag and Drop */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Upload Documents
              </label>
              <div
                className={`relative border-2 border-dashed rounded-lg p-6 transition-colors ${
                  dragActive
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-300 hover:border-gray-400"
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  accept="image/*,.pdf,.doc,.docx"
                />
                <div className="text-center">
                  <Upload className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-600">
                    <span className="font-semibold text-blue-600">
                      Click to upload
                    </span>{" "}
                    or drag and drop
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    PNG, JPG, PDF, DOC up to 10MB
                  </p>
                </div>
              </div>

              {/* File List */}
              {files.length > 0 && (
                <div className="mt-4 space-y-2">
                  {files.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        <FileText className="h-5 w-5 text-blue-600" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {file.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatFileSize(file.size)}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </form>
        </div>

        {/* Footer - Fixed */}
        <div className="border-t p-6 bg-gray-50">
          <div className="flex justify-end space-x-3">

            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="claim-form"
              disabled={submitting}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {submitting ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Submitting...
                </>
              ) : (
                "Submit Claim"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

  // Claim Details Modal with Tabs
  const ClaimDetailsModal = () => {
    if (!selectedClaim || !claimDetails) return null;

    // Extract data from API response
    const claim = claimDetails;
    const documents = claim.documents || [];
    const latestReview = claim.reviews && claim.reviews.length > 0 ? claim.reviews[claim.reviews.length - 1] : null;
    const aiAnalysis = latestReview?.ai_analysis || null;
    const patientInfo = aiAnalysis?.patient_information || {};
    const insuranceInfo = aiAnalysis?.insurance_information || {};
    const claimItems = aiAnalysis?.claim_items || [];
    const totals = aiAnalysis?.totals || { claimed: 0, approved: 0, denied: 0 };
    const payment = claim.payment || null;

    // Build timeline
    const timeline = [
      { 
        step: 1, 
        title: "Claim Submitted", 
        timestamp: claim.created_at,
        status: "completed",
        description: "Claim submitted by member"
      },
      latestReview && {
        step: 2, 
        title: "AI Review Completed", 
        timestamp: latestReview.created_at,
        status: "completed",
        description: `AI confidence: ${latestReview.ai_confidence}%`
      },
      { 
        step: 3, 
        title: claim.status === "approved" || claim.status === "paid" ? "Claim Approved" : claim.status === "denied" ? "Claim Denied" : "Under Review", 
        timestamp: claim.status === "pending" ? null : claim.updated_at,
        status: claim.status === "pending" || claim.status === "under_review" ? "current" : "completed",
        description: claim.status === "pending" || claim.status === "under_review" ? "Awaiting admin review" : "Decision recorded"
      },
      payment && {
        step: 4, 
        title: payment.status === "completed" || payment.status === "paid" ? "Payment Completed" : "Payment Processing", 
        timestamp: payment.processed_at,
        status: payment.status === "completed" || payment.status === "paid" ? "completed" : payment.status === "processing" ? "current" : "pending",
        description: payment.status === "completed" ? `Payment of $${payment.amount} processed` : "Payment being processed"
      },
    ].filter(Boolean);

    if (detailsLoading) {
      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading claim details...</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex justify-between items-center p-6 border-b">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                Claim Review • {claim.claim_number}
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {claim.provider_name}
              </p>
            </div>
            <button
              onClick={() => {
                setDetailsModalOpen(false);
                setSelectedClaim(null);
                setClaimDetails(null);
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Tabs */}
          <div className="border-b">
            <div className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab("submission")}
                className={`py-4 border-b-2 font-medium text-sm ${
                  activeTab === "submission"
                    ? "border-blue-600 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                Claim Submission
              </button>
              <button
                onClick={() => setActiveTab("ai-review")}
                className={`py-4 border-b-2 font-medium text-sm ${
                  activeTab === "ai-review"
                    ? "border-blue-600 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
                disabled={!aiAnalysis}
              >
                AI Review {!aiAnalysis && "(Not Available)"}
              </button>
              <button
                onClick={() => setActiveTab("timeline")}
                className={`py-4 border-b-2 font-medium text-sm ${
                  activeTab === "timeline"
                    ? "border-blue-600 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                Timeline
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Claim Submission Tab */}
            {activeTab === "submission" && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3">Claim Information</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Claim Number:</span>
                        <span className="text-sm font-medium text-gray-900">{claim.claim_number}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Claim Type:</span>
                        <span className="text-sm font-medium text-gray-900 capitalize">{claim.claim_type}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Member:</span>
                        <span className="text-sm font-medium text-gray-900">{claim.member?.user?.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Member Number:</span>
                        <span className="text-sm font-medium text-gray-900">{claim.member?.member_number}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Policy:</span>
                        <span className="text-sm font-medium text-gray-900">{claim.policy?.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Status:</span>
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${getStatusColor(claim.status)}`}>
                          {claim.status}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3">Provider & Amount</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Provider Name:</span>
                        <span className="text-sm font-medium text-gray-900">{claim.provider_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Incident Date:</span>
                        <span className="text-sm font-medium text-gray-900">
                          {new Date(claim.incident_date).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Amount Claimed:</span>
                        <span className="text-lg font-bold text-gray-900">${claim.amount_claimed}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Submitted:</span>
                        <span className="text-sm font-medium text-gray-900">
                          {new Date(claim.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Description</h4>
                  <p className="text-sm text-gray-700">{claim.description}</p>
                </div>

   <div className="bg-gray-50 p-4 rounded-lg">
  <h4 className="font-semibold text-gray-900 mb-3">Attached Documents</h4>
  {documents.length > 0 ? (
    <div className="space-y-2">
      {documents.map((doc, index) => {
        const fileUrl = `${STORAGE_BASE_URL}${doc.file_path}`;
        
        return (
          <div key={index} className="flex items-center justify-between p-3 bg-white rounded border border-gray-200 hover:border-blue-300 transition-colors">
            <div className="flex items-center space-x-3">
              <FileText className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-sm font-medium text-gray-900">{doc.file_name}</p>
                <p className="text-xs text-gray-500">{formatFileSize(doc.file_size)}</p>
              </div>
            </div>
            <div className="flex space-x-2">
              <button 
                onClick={() => window.open(fileUrl, '_blank')}
                className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded"
              >
                View
              </button>
              
                <a href={fileUrl}
                download={doc.file_name}
                className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-50 rounded flex items-center"
              >
                <Download className="h-4 w-4 mr-1" />
                Download
              </a>
            </div>
          </div>
        );
      })}
    </div>
  ) : (
    <p className="text-sm text-gray-500">No documents attached</p>
  )}
</div>

              </div>
            )}

            {/* AI Review Tab */}
            {activeTab === "ai-review" && (
              <div className="space-y-6">
                {aiAnalysis ? (
                  <>
                    {/* AI Confidence & Fraud Score */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-200">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-blue-900">AI Confidence</h4>
                          <CheckCircle className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="text-3xl font-bold text-blue-900">{latestReview.ai_confidence}%</div>
                        <p className="text-sm text-blue-700 mt-1">Recommendation: {latestReview.ai_recommendation}</p>
                      </div>

                      <div className="bg-orange-50 p-4 rounded-lg border-2 border-orange-200">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-orange-900">Fraud Probability</h4>
                          <AlertCircle className="h-5 w-5 text-orange-600" />
                        </div>
                        <div className="text-3xl font-bold text-orange-900">{100 - latestReview.ai_confidence}%</div>
                        <p className="text-sm text-orange-700 mt-1">
                          {latestReview.ai_confidence >= 80 ? "Low risk" : latestReview.ai_confidence >= 60 ? "Medium risk" : "High risk"} detected
                        </p>
                      </div>
                    </div>

                    {/* Patient Information */}
                    {patientInfo && Object.keys(patientInfo).length > 0 && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-3">Patient Information</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-gray-500">Name</p>
                            <p className="text-sm font-medium text-gray-900">{patientInfo.name}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Birth Date</p>
                            <p className="text-sm font-medium text-gray-900">{patientInfo.birth_date}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Address</p>
                            <p className="text-sm font-medium text-gray-900">{patientInfo.address}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Phone</p>
                            <p className="text-sm font-medium text-gray-900">{patientInfo.phone}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Patient Relationship</p>
                            <p className="text-sm font-medium text-gray-900">{patientInfo.relationship} ☑</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Insurance Information */}
                    {insuranceInfo && Object.keys(insuranceInfo).length > 0 && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-3">Insurance Information</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-gray-500">Insurance Company</p>
                            <p className="text-sm font-medium text-gray-900">
                              {insuranceInfo.company} {insuranceInfo.company_code && `(${insuranceInfo.company_code})`}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Insured's Name</p>
                            <p className="text-sm font-medium text-gray-900">{insuranceInfo.insured_name}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Insured's Address</p>
                            <p className="text-sm font-medium text-gray-900">{insuranceInfo.insured_address}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Insured's ID Number</p>
                            <p className="text-sm font-medium text-gray-900">{insuranceInfo.insured_id}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Insurance Plan</p>
                            <p className="text-sm font-medium text-gray-900">{insuranceInfo.plan}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Claim Items Review */}
                    {claimItems.length > 0 && (
                      <div className="bg-white border rounded-lg overflow-hidden">
                        <div className="bg-gray-50 px-4 py-3 border-b">
                          <h4 className="font-semibold text-gray-900">Claim Items</h4>
                        </div>
                        <table className="min-w-full">
                          <thead className="bg-gray-50 border-b">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">CPT/HCPCS</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Decision</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {claimItems.map((item, index) => (
                              <tr key={index} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.cpt}</td>
                                <td className="px-4 py-3">
                                  <p className="text-sm text-gray-900">{item.description}</p>
                                  <p className="text-xs text-gray-500">{item.validation}</p>
                                </td>
                                <td className="px-4 py-3 text-sm font-medium text-gray-900">${parseFloat(item.amount).toFixed(2)}</td>
                                <td className="px-4 py-3">
                                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                    item.decision === "approve" 
                                      ? "bg-green-100 text-green-800" 
                                      : "bg-red-100 text-red-800"
                                  }`}>
                                    {item.reason}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div className="bg-gray-50 px-4 py-3 border-t flex justify-between items-center">
                          <div className="text-sm text-gray-600">
                            <span className="font-medium">Total Claimed: ${parseFloat(totals.claimed).toFixed(2)}</span>
                          </div>
                          <div className="text-sm">
                            <span className="text-green-600 font-medium">Approved: ${parseFloat(totals.approved).toFixed(2)}</span>
                            <span className="mx-2 text-gray-400">|</span>
                            <span className="text-red-600 font-medium">Denied: ${parseFloat(totals.denied).toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* AI Assistant Note */}
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-medium">
                            AI
                          </div>
                        </div>
                        <div className="flex-1">
                          <h5 className="font-semibold text-blue-900 mb-1">AI Assistant</h5>
                          <p className="text-sm text-blue-800">{latestReview.notes}</p>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12">
                    <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-500">AI Review not available for this claim yet.</p>
                  </div>
                )}
              </div>
            )}

            {/* Timeline Tab */}
            {activeTab === "timeline" && (
              <div className="space-y-6">
                <div className="flex items-center justify-between mb-6">
                  <h4 className="font-semibold text-gray-900">Claim Timeline</h4>
                  <p className="text-sm text-gray-500">Claim ID: {claim.claim_number}</p>
                </div>

                {/* Visual Timeline */}
                <div className="relative">
                  <div className="absolute top-6 left-6 h-full w-0.5 bg-gray-200"></div>
                  
                  <div className="space-y-8">
                  

                      {timeline.map((item, index) => (
                      <div key={index} className="relative flex items-start space-x-4">
                        <div className={`relative z-10 flex items-center justify-center w-12 h-12 rounded-full border-4 ${
                          item.status === "completed" 
                            ? "bg-green-500 border-green-200" 
                            : item.status === "current"
                            ? "bg-blue-500 border-blue-200 animate-pulse"
                            : "bg-gray-300 border-gray-200"
                        }`}>
                          <span className="text-white font-bold">{item.step}</span>
                        </div>
                        
                        <div className="flex-1 pb-8">
                          <div className="bg-white border rounded-lg p-4 shadow-sm">
                            <div className="flex justify-between items-start mb-2">
                              <h5 className="font-semibold text-gray-900">{item.title}</h5>
                              {item.status === "completed" && (
                                <CheckCircle className="h-5 w-5 text-green-600" />
                              )}
                              {item.status === "current" && (
                                <Clock className="h-5 w-5 text-blue-600" />
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mb-2">{item.description}</p>
                            {item.timestamp && (
                              <p className="text-xs text-gray-500">
                                {new Date(item.timestamp).toLocaleString()}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* AI Assistant Timeline Message */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-center text-white text-sm font-bold">
                        AI
                      </div>
                    </div>
                    <div className="flex-1">
                      <h5 className="font-semibold text-gray-900 mb-1">AI Assistant</h5>
                      <p className="text-sm text-gray-700">
                        {claim.status === "approved" || claim.status === "paid"
                          ? `Your ${claim.claim_type} claim has been approved. Payment of $${claim.amount_claimed} will be sent to your provider within 3 days.`
                          : claim.status === "denied"
                          ? `Your claim has been reviewed and denied. Please check the review notes for more information.`
                          : `Your claim is currently under review. ${latestReview ? `Our AI analysis shows ${latestReview.ai_confidence}% confidence.` : ''} You will be notified once a decision is made.`
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          {userRole === "admin" && (
            <div className="border-t p-6 bg-gray-50">
              <div className="flex justify-between items-center">
                <button
                  onClick={() => {
                    setDetailsModalOpen(false);
                    setSelectedClaim(null);
                    setClaimDetails(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Close
                </button>
                <div className="flex space-x-3">
                  {(claim.status === "pending" || claim.status === "under_review") && (
                    <>
                      <button
                        onClick={() => handleReviewClaim(claim.id, "deny")}
                        className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Deny
                      </button>
                      <button
                        onClick={() => handleReviewClaim(claim.id, "approve")}
                        className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
                      >
                        <Check className="h-4 w-4 mr-2" />
                        Approve
                      </button>
                    </>
                  )}
                  {claim.status === "approved" && (
                    <button
                      onClick={() => handleReleasePayment(claim.id)}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Release Payment
                    </button>
                  )}
                  {claim.status === "processing" && payment && (
                    <button
                      onClick={() => handleReviewClaim(claim.id, "paid")}
                      className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Mark as Paid
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) return <div className="p-6">Loading claims...</div>;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            {userRole === "admin" ? "Claims Management" : "My Claims"}
          </h2>
          <p className="text-gray-600 mt-1">
            {userRole === "admin"
              ? "Review and manage insurance claims"
              : "Track your submitted claims"}
          </p>
        </div>

        <button
          onClick={() => setShowSubmitForm(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          Submit Claim
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Claim
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Member
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                {userRole === "admin" && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    AI Review
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {claims?.map((claim) => (
                <tr key={claim.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {claim.claim_number}
                      </div>
                      <div className="text-sm text-gray-500">
                        {claim.claim_type}
                      </div>
                      <div className="text-xs text-gray-400">
                        {new Date(claim.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {claim.member?.user?.name}
                      <br />
                      {claim.member?.member_number}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      ${claim.amount_claimed}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                        claim.status
                      )}`}
                    >
                      {claim.status}
                    </span>
                  </td>

                  {userRole === "admin" && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      {claim.reviews && claim.reviews.length > 0 ? (
                        <div className="flex items-center">
                          <div
                            className={`w-2 h-2 rounded-full mr-2 ${
                              claim.reviews[0].ai_confidence >= 80
                                ? "bg-green-500"
                                : claim.reviews[0].ai_confidence >= 60
                                ? "bg-yellow-500"
                                : "bg-red-500"
                            }`}
                          ></div>
                          <span className="text-xs text-gray-600">
                            {claim.reviews[0].ai_confidence}% - {claim.reviews[0].ai_recommendation}
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 italic">Not Yet Reviewed</span>
                      )}
                    </td>
                  )}

                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => openDetailsModal(claim)}
                        className="text-blue-600 hover:text-blue-900 flex items-center"
                        title="See Details"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Details
                      </button>
                      {userRole === "admin" && (claim.status === "pending" || claim.status === "under_review") && (
                        <>
                          <button
                            onClick={() => handleReviewClaim(claim.id, "approve")}
                            className="text-green-600 hover:text-green-900"
                            title="Approve"
                          >
                            <Check className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleReviewClaim(claim.id, "deny")}
                            className="text-red-600 hover:text-red-900"
                            title="Deny"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </>
                      )}
                      {userRole === "admin" && claim.status === "approved" && (
                        <button
                          onClick={() => handleReleasePayment(claim.id)}
                          className="text-purple-600 hover:text-purple-900 flex items-center text-xs"
                          title="Release Payment"
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Pay
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showSubmitForm && (
        <ClaimSubmissionForm onClose={() => setShowSubmitForm(false)} />
      )}

      {detailsModalOpen && <ClaimDetailsModal />}
    </div>
  );
};

export default ClaimsManager;